import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

export const AudioTranscriptionContext = createContext(null);

export const useAudioTranscription = () => {
    const ctx = useContext(AudioTranscriptionContext);
    if (!ctx) {
        throw new Error('useAudioTranscription must be used within an AudioTranscriptionContext.Provider');
    }
    return ctx;
};

const getToastStyle = () => ({
    backgroundColor: 'white',
    color: 'red',
    fontSize: '13px',
    padding: '5px 7px',
    fontWeight: '500',
});

const useAudioTranscriptionStore = ({
    textAreaRef,
    botId,
    orgId,
    t,
}) => {
    const [sttProvider, setSttProvider] = useState('');
    const [sttModel, setSttModel] = useState('');
    const [voiceNotesEnabled, setVoiceNotesEnabled] = useState(false);

    const [isRecording, setIsRecording] = useState(false);
    const [isProcessingAudio, setIsProcessingAudio] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [transcriptionTick, setTranscriptionTick] = useState(0);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamRef = useRef(null);

    const stopAudioLevelMonitoring = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => { });
            audioContextRef.current = null;
            analyserRef.current = null;
        }
        setAudioLevel(0);
    }, []);

    const startAudioLevelMonitoring = useCallback((stream) => {
        try {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);

            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

            const update = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);
                const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                setAudioLevel(Math.min(100, avg * 2));
                animationFrameRef.current = requestAnimationFrame(update);
            };

            update();
        } catch (error) {
            console.error('AudioContext error:', error);
        }
    }, []);

    const convertBlobToBase64 = useCallback((blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }, []);

    const sendAudioToSTT = useCallback(async (audioBlob) => {
        try {
            setIsProcessingAudio(true);
            const base64Audio = await convertBlobToBase64(audioBlob);

            const response = await axios.post(
                `${process.env.REACT_APP_VOICE_API_URL}/v1/voice-notes/stt`,
                {
                    audio_data: base64Audio,
                    bot_id: `bot_${botId}`,
                    ...(sttProvider ? { stt_provider: String(sttProvider).toLowerCase() } : {}),
                    ...(sttModel ? { stt_model: String(sttModel).toLowerCase() } : {}),
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Api-token': process.env.REACT_APP_BOT_API_KEY,
                        ...(orgId ? { 'X-ORG-ID': orgId } : {}),
                    },
                }
            );

            const transcribedText =
                response?.data?.text ||
                response?.data?.transcript ||
                response?.data?.data?.text ||
                '';

            if (transcribedText && textAreaRef?.current) {
                const currentText = textAreaRef.current.value;
                const updatedText = currentText.trim() ? `${currentText.trim()} ${transcribedText}` : transcribedText;
                textAreaRef.current.value = updatedText;
                setTranscriptionTick((prev) => prev + 1);
                textAreaRef.current.focus();
            } else {
                toast.error(t('Could not transcribe audio. Please try again.'), {
                    position: 'top-right',
                    style: getToastStyle(),
                });
            }
        } catch (error) {
            console.error('STT API error:', error);
            toast.error(t('Audio transcription failed. Please try again.'), {
                position: 'top-right',
                style: getToastStyle(),
            });
        } finally {
            setIsProcessingAudio(false);
        }
    }, [botId, convertBlobToBase64, orgId, sttModel, sttProvider, t, textAreaRef]);

    const handleStartRecording = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            audioChunksRef.current = [];

            const options = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? { mimeType: 'audio/webm;codecs=opus' }
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? { mimeType: 'audio/webm' }
                    : {};

            const recorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                const mimeType = recorder.mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }

                stopAudioLevelMonitoring();
                await sendAudioToSTT(audioBlob);
            };

            recorder.start(100);
            startAudioLevelMonitoring(stream);
            setIsRecording(true);
        } catch (error) {
            console.error('Microphone access error:', error);
            toast.error(t('Microphone access was denied. Please enable microphone permissions.'), {
                position: 'top-right',
                style: getToastStyle(),
            });
        }
    }, [sendAudioToSTT, startAudioLevelMonitoring, stopAudioLevelMonitoring, t]);

    const handleStopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
    }, []);

    const barScale = useCallback((index) => {
        const arc = [0.5, 0.65, 0.85, 1.0, 0.85, 0.65, 0.5];
        const level = Math.max(0.65, audioLevel / 100);
        return (arc[index] * level).toFixed(2);
    }, [audioLevel]);

    useEffect(() => {
        return () => {
            stopAudioLevelMonitoring();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [stopAudioLevelMonitoring]);

    return {
        sttProvider,
        setSttProvider,
        sttModel,
        setSttModel,
        voiceNotesEnabled,
        setVoiceNotesEnabled,

        isRecording,
        isProcessingAudio,
        transcriptionTick,
        handleStartRecording,
        handleStopRecording,
        barScale,
    };
};

export default useAudioTranscriptionStore;

